import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCheckout } from '../CheckoutContext';
import { preferredLang, useLang } from '../i18n/LanguageContext';
import offer from '../config/offer';
import { useCountry } from '../lib/geo';
import './OpenBroEat.css';

/**
 * /eat : landing du Reel Instagram US / Canada (« All I wanted was abs… »). Variante `nutrition_v2` (lib/landing.js).
 * La page est la suite du Reel : le physique de Gaut, ses repas, et le système pour manger comme lui. Rien d'autre d'OpenBro.
 * Visuels : uniquement les reels de Gaut (public/ugc) et des images tirées de ces reels (public/eat).
 * Prix : le même chiffre partout (offer.eatPrices = EAT_LAUNCH_CENTS / EAT_CENTS du Worker). 29 jusqu'à offer.launchEndsAt, puis 99,
 * puis 29 depuis le 28/10/2026. Barré et compte à rebours seulement pendant une offre (avant offer.launchEndsAt), barré en euros seulement.
 * Pas de rareté inventée : la vraie fin, le prix d'après, et le prix gardé tant qu'on reste abonné (vrai chez Stripe).
 * Langue : anglais ou français selon le navigateur (pas la règle « Canada → français » du reste du site), bouton FR / EN.
 * Stripe et la page après paiement suivent la langue affichée.
 * En bas (après la fin, avant le footer) : passerelle « OpenBro Elite » vers /elite, pour ceux qui veulent l'IA / le business.
 * Titres A / B / C : ?h=b ou ?h=c pour les voir ; A est le contrôle. Aucun tirage automatique.
 * /eatv2 (variante `eat_v2`) : la même page sans « / mois » collé aux prix ni dans les accroches (`noMonthly`).
 * Seule différence avec /eat, pour que la comparaison du cockpit mesure ça. L'abonnement reste annoncé
 * (« Abonnement · résiliable… » sous les boutons, petites lignes du bloc prix) : obligatoire avant achat, et Stripe affiche « par mois ».
 */

const SUPPORT_EMAIL = 'mia@openpro.ai';
const REEL = '/ugc/gaut-smoothie.mp4';

const COPY = {
  en: {
    switchTo: 'FR',
    switchLabel: 'Voir la page en français',
    login: 'Log in',
    title: 'Eat like Gaut · OpenBro',
    headlines: {
      a: { top: 'Eat like Gaut.', sub: ['Build the body.', 'Keep the food.'] },
      b: { top: 'Get lean.', sub: ['Don’t eat boring.'] },
      c: { top: 'The food I eat', sub: ['to stay lean.'] },
    },
    // 28/10/2026 : comme en français, l'accroche s'adresse au visiteur.
    lead: <>Your <b>weekly meal plan</b>, your <b>shopping list</b> and your <b>30-day system</b>.</>,
    perMonth: '/ month',
    renews: 'renews monthly',
    // Une seule mensualité : le prix monte pour ceux qui attendent, pas pour ceux qui ont rejoint (vrai chez Stripe).
    bar: ({ price, next }) => <>Launch price <b>{price}/month</b> · goes up to {next} in</>,
    barShort: ({ next }) => <>Price goes up to <b>{next}</b> in</>,
    goesUpIn: 'Price goes up in',
    lock: ({ price, day }) => <>Join before {day} and <b>you keep {price}/month</b> for as long as you stay.</>,
    regular: ({ next }) => `regular price ${next}`,
    off: ({ pct }) => `−${pct}%`,
    oneCharge: 'One payment a month · Cancel anytime',
    dealPoints: ({ price, next, end }) => [
      <><b>{price}/month</b>, locked in for as long as you stay</>,
      <>From {end}, new members pay {next}/month</>,
      <>One payment a month. Cancel anytime.</>,
    ],
    finalUp: ({ day }) => `goes up ${day}`,
    stickyUp: 'goes up in',
    clock: { d: 'd', h: 'h', m: 'm', s: 's' },
    start: 'Start now',
    opening: 'Opening checkout…',
    openingShort: 'Opening…',
    micro: 'Instant access · Cancel anytime',
    includedTitle: <>Everything you need.<br /><span>Not another diet.</span></>,
    included: [
      ['Gaut’s recipes', 'Meals that actually look like food you want to eat.'],
      ['Weekly meal plan', 'Know what you’re eating before the week starts.'],
      ['Shopping list', 'Every Monday, know exactly what to buy.'],
      ['30-day system', 'Follow the plan instead of guessing every day.'],
    ],
    shoppingShot: '/v10/v70-courses-mac-en.jpg',
    dietTitle: 'This is the diet.',
    meals: ['Waffle burgers', 'Smoothie bowl', 'Eggs & fruit', 'Pancake stack', 'Pizza', 'Taco bowl', 'Coconut bowl', 'Pancakes & berries', 'Pancake bites'],
    manifest: ['No sad chicken and rice.', 'No wondering what to eat.', 'Just a system you can actually follow.'],
    howTitle: 'How it works',
    steps: [['Join', 'Get instant access.'], ['Follow', 'Recipes, quantities, weekly plan and shopping list.'], ['Repeat', 'Follow the system for the next 30 days.']],
    gautAlt: 'Gaut cooking waffle burgers in his kitchen',
    gautTitle: <>I wasted years<br />figuring this out.<br />You don’t have to.</>,
    gaut: [
      'I wanted to build my body without eating boring food every day.',
      'So I kept the meals I actually like, and I turned them into a simple system: the recipes, the portions, the weekly plan and the shopping list.',
      'Now you can follow it too.',
    ],
    pricingTitle: 'Start today.',
    pricingSub: 'Everything above. Instant access.',
    getAccess: 'Get access',
    cancelAnytime: 'Cancel anytime.',
    faqTitle: 'Questions',
    faq: [
      ['What do I get?', 'Gaut’s recipes with the quantities, a weekly meal plan, a shopping list every Monday and a 30-day plan to follow. Everything is in your member area.'],
      ['Is this a strict diet?', 'No. Burgers, pizza, tacos, pancakes: real meals, made with the right ingredients and portions. Nothing is banned, nothing to weigh at every meal.'],
      ['Can I cancel anytime?', `Yes. No commitment. Email ${SUPPORT_EMAIL} and your subscription stops renewing. It ends at the end of the month you already paid for.`],
      ['Do I get access immediately?', 'Yes. Right after payment, you create your account and you’re in.'],
    ],
    finalTitle: <>You already know<br />what doing nothing<br /><span>gets you.</span></>,
    join: 'Join OpenBro',
    // Passerelle vers /elite (OpenBroV60 : appel mensuel avec Gaut, 6 campus dont IA et e-commerce, communauté).
    elite: {
      title: <>Want more<br /><span>than abs?</span></>,
      text: 'AI, e-commerce, making money online. A private call with Gaut every month, 6 campuses and a private community.',
      cta: 'Discover OpenBro Elite',
    },
    terms: 'Terms',
    privacy: 'Privacy',
  },
  fr: {
    switchTo: 'EN',
    switchLabel: 'See this page in English',
    login: 'Connexion',
    title: 'Mange comme Gaut · OpenBro',
    headlines: {
      a: { top: 'Mange comme Gaut.', sub: ['Construis ton physique.', 'Garde le plaisir de manger.'] },
      b: { top: 'Deviens sec.', sub: ['Sans manger triste.'] },
      c: { top: 'Ce que je mange', sub: ['pour rester sec.'] },
    },
    // 28/10/2026 : Gaut veut que l'accroche s'adresse au visiteur (« ton », « ta ») plutôt qu'à lui-même.
    lead: <>Ton <b>meal plan de la semaine</b>, ta <b>liste de courses</b> et ton <b>système sur 30{'\u00a0'}jours</b>.</>,
    perMonth: '/ mois',
    renews: 'renouvelé chaque mois',
    bar: ({ price, next }) => <>Prix de lancement <b>{price}/mois</b> · passe à {next} dans</>,
    barShort: ({ next }) => <>Le prix passe à <b>{next}</b> dans</>,
    goesUpIn: 'Le prix augmente dans',
    lock: ({ price, day }) => <>Rejoins avant {day} et <b>tu gardes {price}/mois</b> tant que tu restes.</>,
    regular: ({ next }) => `prix normal ${next}`,
    off: ({ pct }) => `−${pct} %`,
    oneCharge: 'Un seul paiement par mois · Résiliable à tout moment',
    dealPoints: ({ price, next, end }) => [
      <><b>{price}/mois</b>, bloqué tant que tu restes abonné</>,
      <>À partir du {end}, les nouveaux membres paient {next}/mois</>,
      <>Un seul paiement par mois. Résiliable à tout moment.</>,
    ],
    finalUp: ({ day }) => `augmente ${day}`,
    stickyUp: 'augmente dans',
    clock: { d: 'j', h: 'h', m: 'min', s: 's' },
    start: 'Commencer maintenant',
    opening: 'Ouverture du paiement…',
    openingShort: 'Ouverture…',
    micro: 'Accès immédiat · Résiliable à tout moment',
    includedTitle: <>Tout ce qu’il te faut.<br /><span>Pas un régime de plus.</span></>,
    included: [
      ['Les recettes de Gaut', 'Des plats qui donnent vraiment envie.'],
      ['Le meal plan de la semaine', 'Tu sais ce que tu manges avant que la semaine commence.'],
      ['La liste de courses', 'Chaque lundi, tu sais exactement quoi acheter.'],
      ['Le système 30 jours', 'Tu suis le plan au lieu de deviner chaque jour.'],
    ],
    shoppingShot: '/v10/v70-courses-mac.jpg',
    dietTitle: 'C’est ça, le régime.',
    meals: ['Burgers gaufre', 'Smoothie bowl', 'Œufs & fruits', 'Pancakes', 'Pizza', 'Taco bowl', 'Bol coco', 'Pancakes & fruits rouges', 'Mini pancakes'],
    manifest: ['Pas de riz-poulet triste.', 'Plus besoin de te demander quoi manger.', 'Juste un système que tu peux vraiment suivre.'],
    howTitle: 'Comment ça marche',
    steps: [['Rejoins', 'Accès immédiat.'], ['Suis', 'Recettes, quantités, plan de la semaine et liste de courses.'], ['Répète', 'Suis le système pendant 30 jours.']],
    gautAlt: 'Gaut prépare des burgers gaufre dans sa cuisine',
    gautTitle: <>J’ai perdu des années<br />à comprendre ça.<br />Toi, tu n’as pas à le faire.</>,
    gaut: [
      'Je voulais construire mon physique sans manger fade tous les jours.',
      'Alors j’ai gardé les plats que j’aime vraiment, et j’en ai fait un système simple : les recettes, les portions, le plan de la semaine et la liste de courses.',
      'Maintenant, tu peux le suivre aussi.',
    ],
    pricingTitle: 'Commence aujourd’hui.',
    pricingSub: 'Tout ce qu’il y a au-dessus. Accès immédiat.',
    getAccess: 'J’y accède',
    cancelAnytime: 'Résiliable à tout moment.',
    faqTitle: 'Questions',
    faq: [
      ['Qu’est-ce que je reçois ?', 'Les recettes de Gaut avec les quantités, un meal plan pour la semaine, une liste de courses chaque lundi et un plan sur 30 jours. Tout est dans ton espace membre.'],
      ['C’est un régime strict ?', 'Non. Burgers, pizza, tacos, pancakes : de vrais plats, faits avec les bons ingrédients et les bonnes portions. Rien d’interdit, rien à peser à chaque repas.'],
      ['Je peux résilier quand je veux ?', `Oui. Sans engagement. Écris à ${SUPPORT_EMAIL} et ton abonnement ne se renouvelle plus. Il s’arrête à la fin du mois déjà payé.`],
      ['J’ai accès tout de suite ?', 'Oui. Juste après le paiement, tu crées ton compte et tu es dedans.'],
    ],
    finalTitle: <>Tu sais déjà<br />ce que ne rien faire<br /><span>te rapporte.</span></>,
    join: 'Rejoindre OpenBro',
    elite: {
      title: <>Tu veux plus<br /><span>que des abdos ?</span></>,
      text: 'IA, e-commerce, gagner de l’argent en ligne. Un appel privé avec Gaut chaque mois, 6 campus et une communauté privée.',
      cta: 'Découvrir OpenBro Elite',
    },
    terms: 'Conditions',
    privacy: 'Confidentialité',
  },
};

const GAUT_PHOTOS = [
  { src: '/eat/gaut-kitchen.jpg?v=20', pos: 'center 82%', alt: { en: 'Gaut finishing waffle burgers', fr: 'Gaut termine des burgers gaufre' } },
  { src: '/eat/abs-fruits.jpg', pos: 'center 58%', alt: { en: 'A fruit bowl next to Gaut', fr: 'Un bol de fruits à côté de Gaut' } },
  { src: '/eat/garden-bowl.jpg', pos: 'center 62%', alt: { en: 'Gaut holding a plate of chicken, mango and vegetables', fr: 'Gaut tient une assiette de poulet, mangue et légumes' } },
  { src: '/eat/shower.jpg', pos: 'center center', alt: { en: 'Gaut under an outdoor shower', fr: 'Gaut sous une douche en extérieur' } },
];

/** /eatv2 : textes qui remplacent ceux de COPY quand la page n'affiche pas « par mois ». */
const NO_MONTH = {
  en: {
    perMonth: '',
    renews: 'subscription',
    bar: ({ price, next }) => <>Launch price <b>{price}</b> · goes up to {next} in</>,
    lock: ({ price, day }) => <>Join before {day} and <b>you keep {price}</b> for as long as you stay.</>,
    oneCharge: 'Subscription · Cancel anytime',
    micro: 'Instant access · Subscription, cancel anytime',
    dealPoints: ({ price, next, end }) => [
      <><b>{price}</b>, locked in for as long as you stay</>,
      <>From {end}, new members pay {next}</>,
      <>No commitment. Cancel anytime.</>,
    ],
  },
  fr: {
    perMonth: '',
    renews: 'abonnement',
    bar: ({ price, next }) => <>Prix de lancement <b>{price}</b> · passe à {next} dans</>,
    lock: ({ price, day }) => <>Rejoins avant {day} et <b>tu gardes {price}</b> tant que tu restes.</>,
    oneCharge: 'Abonnement · Résiliable à tout moment',
    micro: 'Accès immédiat · Abonnement résiliable à tout moment',
    dealPoints: ({ price, next, end }) => [
      <><b>{price}</b>, bloqué tant que tu restes abonné</>,
      <>À partir du {end}, les nouveaux membres paient {next}</>,
      <>Sans engagement. Résiliable à tout moment.</>,
    ],
  },
};

// Bloc OpenBro Elite : couvertures des 6 campus de l'espace membre (mêmes fichiers que CAMPUS dans OpenBroV60.jsx).
// IA et e-commerce d'abord : c'est ce que le bloc promet.
const ELITE_CAMPUS = [
  { src: '/v10/campus-ia.jpg', srcEn: '/v10/campus-ia-en.jpg?v=2', alt: { fr: 'Campus IA & Automatisation', en: 'AI & Automation campus' } },
  { src: '/v10/campus-reseaux.jpg', srcEn: '/v10/campus-reseaux-en.jpg?v=2', alt: { fr: 'Campus E-commerce & Réseaux sociaux', en: 'Social media & e-commerce campus' } },
  { src: '/v10/campus-montage.jpg', alt: { fr: 'Campus Montage vidéo', en: 'Video editing campus' } },
  { src: '/v10/campus-filming.jpg', alt: { fr: 'Campus Tournage & Filming', en: 'Filming campus' } },
  { src: '/v10/campus-musculation.jpg', srcEn: '/v10/campus-musculation-en.jpg?v=2', alt: { fr: 'Campus Musculation & Discipline', en: 'Training & discipline campus' } },
  { src: '/v10/campus-nutrition.jpg', alt: { fr: 'Campus Nutrition & Performance', en: 'Nutrition & performance campus' } },
];

const INCLUDED_MEDIA = [
  { img: '/eat/burger.jpg', pos: 'center 55%' },
  { img: '/formations/ig-food.jpg', pos: 'center 68%' },
  { img: null, pos: 'center 50%' },
  { img: '/eat/dates-plate.jpg', pos: 'center 58%' },
];

// Mosaïque : vrais plats des reels de Gaut, dans l'ordre de COPY.*.meals. Une tuile vidéo (muette, lue seulement à l'écran).
const MEALS = [
  { img: '/eat/burger.jpg', pos: 'center 55%', tall: true },
  { img: '/eat/bowl.jpg', pos: 'center 45%' },
  { img: '/eat/board.jpg', pos: 'center 40%' },
  { video: '/ugc/gaut-pancakes.mp4', img: '/eat/pancake-stack.jpg', pos: 'center 60%', tall: true },
  { img: '/eat/pizza.jpg', pos: 'center 50%' },
  { img: '/eat/taco-bowl.jpg', pos: 'center 62%' },
  { img: '/eat/pink-bowl.jpg?v=2', pos: 'center 56%', tall: true, wide: true },
  { img: '/eat/pancakes-berries.jpg', pos: 'center 45%' },
  { img: '/eat/pancake-bites.jpg', pos: 'center 60%' },
];

const PRICE_LABEL = {
  en: { usd: (n) => `$${n}`, cad: (n) => `CA$${n}`, eur: (n) => `€${n}` },
  fr: { usd: (n) => `${n} $ US`, cad: (n) => `${n} $ CA`, eur: (n) => `${n} €` },
};

/**
 * Prix affiché dans la devise que Stripe facturera (même règle que eatPricing dans le Worker).
 * Anglais → dollars, français → euros. Canada → dollars canadiens dans les deux langues.
 * `deal` tant que l'offre de lancement court (offer.launchEndsAt), sinon null.
 * Jours et heures dans le fuseau du visiteur (dimanche 23:59 à Paris = dimanche 17:59 à New York).
 */
function useEatPrice(lang, now) {
  const country = useCountry();
  let currency = lang === 'en' ? 'usd' : 'eur';
  if (country === 'CA') currency = 'cad';
  const format = (PRICE_LABEL[lang] || PRICE_LABEL.en)[currency];
  const endsAt = Date.parse(offer.launchEndsAt || '');
  const on = Number.isFinite(endsAt) && now < endsAt;
  const amount = (on ? offer.eatPrices.launch : offer.eatPrices.after)[currency];
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';
  const when = (options) => new Date(endsAt).toLocaleString(locale, options);
  return {
    currency,
    label: format(amount),
    deal: on ? {
      price: format(amount),
      next: format(offer.eatPrices.after[currency]),
      // Barré seulement en euros : 99 € a vraiment été facturé avant le lancement, 99 $ jamais.
      was: currency === 'eur' ? format(offer.eatPrices.after.eur) : null,
      pct: Math.floor((1 - amount / offer.eatPrices.after[currency]) * 100),
      ms: endsAt - now,
      day: when({ weekday: 'long' }),
      dayShort: when({ weekday: 'short' }).replace('.', ''),
      end: when({ weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' }),
    } : null,
  };
}

/** « 6 j 21 h » (barre du bas) ou « 21:56:12 » le dernier jour. */
function leftShort(ms, unit) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  if (days) return `${days} ${unit.d} ${hours} ${unit.h}`;
  return leftLabel(ms, unit);
}

/** « 6 j 18:22:10 » : temps restant jusqu'à la fin réelle de l'offre. */
function leftLabel(ms, unit) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const clock = [Math.floor((total % 86400) / 3600), Math.floor((total % 3600) / 60), total % 60].map((n) => String(n).padStart(2, '0')).join(':');
  return days ? `${days} ${unit.d} ${clock}` : clock;
}

function Arrow() {
  return (
    <svg className="eat-arrow" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}

/** Vidéo muette en boucle, lue seulement quand elle est à l'écran (économise la data sur mobile). */
function LazyVideo({ src, poster, className, eager = false, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (calm) return undefined;
    video.muted = true;
    if (typeof IntersectionObserver === 'undefined') {
      video.play().catch(() => {});
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.2 });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      className={className}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      autoPlay={eager}
      preload={eager ? 'auto' : 'none'}
      disablePictureInPicture
      aria-hidden="true"
      style={style}
    />
  );
}

export default function OpenBroEat({ noMonthly = false, home = '/' }) {
  const { startCheckout, isActive } = useCheckout();
  const { lang: contextLang, forceSessionLang } = useLang();
  // Avant le premier rendu, la langue de la page (navigateur) plutôt que celle du site (Canada → français).
  const [lang, setLang] = useState(() => (preferredLang() === 'fr' ? 'fr' : 'en'));
  const copy = noMonthly ? { ...COPY[lang], ...NO_MONTH[lang] } : COPY[lang];
  const [params] = useSearchParams();
  const headlineId = ['a', 'b', 'c'].includes(String(params.get('h') || '').toLowerCase()) ? String(params.get('h')).toLowerCase() : 'a';
  const headline = copy.headlines[headlineId];
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const price = useEatPrice(lang, now);
  const deal = price.deal;
  const [openFaq, setOpenFaq] = useState(0);
  const [sticky, setSticky] = useState(false);
  const heroCta = useRef(null);
  const endZone = useRef([]);

  // Toute la visite garde la langue de la page (Stripe, paiement, activation), sans changer le choix mémorisé du site.
  // Rejoué si le site change de langue tout seul (pays Cloudflare reçu après coup).
  useEffect(() => {
    forceSessionLang(lang);
    document.documentElement.lang = lang;
    document.title = copy.title;
  }, [lang, contextLang, forceSessionLang, copy.title]);

  // Barre d'achat collée en bas sur mobile : après le bouton du haut, cachée quand le prix ou la fin sont à l'écran.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const state = { pastHero: false, nearEnd: new Set() };
    const update = () => setSticky(state.pastHero && state.nearEnd.size === 0);
    const hero = new IntersectionObserver(([entry]) => {
      state.pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    });
    if (heroCta.current) hero.observe(heroCta.current);
    const end = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) state.nearEnd.add(entry.target);
        else state.nearEnd.delete(entry.target);
      });
      update();
    }, { threshold: 0.15 });
    endZone.current.filter(Boolean).forEach((node) => end.observe(node));
    return () => {
      hero.disconnect();
      end.disconnect();
    };
  }, []);

  const switchLang = () => setLang((current) => (current === 'fr' ? 'en' : 'fr'));

  const join = (source) => {
    if (isActive) return;
    startCheckout(source, null, { locale: lang });
  };

  const cta = (source, label, extra = '') => (
    <button type="button" className={`eat-btn ${extra}`} disabled={isActive} onClick={() => join(source)}>
      {isActive ? copy.opening : label}<Arrow />
    </button>
  );

  return (
    <div className={deal ? 'eat has-bar' : 'eat'} data-headline={headlineId} lang={lang}>
      {deal && (
        <button type="button" className="eat-bar" disabled={isActive} onClick={() => join('eat-bar')}>
          <span className="eat-bar-long">{copy.bar(deal)}</span>
          <span className="eat-bar-short">{copy.barShort(deal)}</span>
          <b className="eat-clock">{leftLabel(deal.ms, copy.clock)}</b>
        </button>
      )}
      <header className="eat-top">
        <Link to={home} className="eat-logo" aria-label="OpenBro"><img src="/logo-openbro.png" alt="OpenBro" /></Link>
        <div className="eat-top-actions">
          <button type="button" className="eat-lang" onClick={switchLang} aria-label={copy.switchLabel}>{copy.switchTo}</button>
          <Link to="/login" className="eat-login">{copy.login}</Link>
        </div>
      </header>

      <section className="eat-hero" id="hero">
        <div className="eat-hero-media">
          <LazyVideo src={REEL} poster="/eat/hook.jpg" className="eat-hero-video" eager />
          <img className="eat-hero-still" src="/eat/bowl.jpg" alt="" aria-hidden="true" />
        </div>
        <div className="eat-hero-copy">
          <h1 className="eat-h1">
            <span>{headline.top}</span>
            <em>{headline.sub.map((line) => <span key={line}>{line}</span>)}</em>
          </h1>
          <p className="eat-lead">{copy.lead}</p>
          {deal ? (
            <>
              <p className="eat-up"><i aria-hidden="true" />{copy.goesUpIn} <b className="eat-clock">{leftLabel(deal.ms, copy.clock)}</b></p>
              <p className="eat-price">
                {deal.was && <s>{deal.was}</s>}<b>{price.label}</b> {copy.perMonth}
                {deal.was ? <em className="eat-off">{copy.off(deal)}</em> : <span className="eat-regular">· {copy.regular(deal)}</span>}
              </p>
              <p className="eat-lock">{copy.lock(deal)}</p>
            </>
          ) : <p className="eat-price"><b>{price.label}</b> {copy.perMonth} · {copy.renews}</p>}
          <div ref={heroCta}>{cta('eat-hero', copy.start, deal ? 'eat-btn-hero is-live' : 'eat-btn-hero')}</div>
          <p className="eat-micro">{deal ? copy.oneCharge : copy.micro}</p>
        </div>
      </section>

      <section className="eat-section" id="included">
        <h2 className="eat-h2">{copy.includedTitle}</h2>
        <ol className="eat-included">
          {copy.included.map(([title, line], index) => {
            const media = INCLUDED_MEDIA[index];
            return (
              <li key={title}>
                <div className="eat-included-img"><img src={media.img || copy.shoppingShot} alt="" loading="lazy" decoding="async" style={{ objectPosition: media.pos }} /></div>
                <p className="eat-num">{String(index + 1).padStart(2, '0')}</p>
                <h3>{title}</h3>
                <p>{line}</p>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="eat-section eat-diet" id="diet">
        <h2 className="eat-h2 eat-center">{copy.dietTitle}</h2>
        <ul className="eat-mosaic">
          {MEALS.map((meal, index) => {
            const label = copy.meals[index];
            return (
              <li key={meal.img} className={[meal.tall && 'is-tall', meal.wide && 'is-wide'].filter(Boolean).join(' ')}>
                {meal.video
                  ? <LazyVideo src={meal.video} poster={meal.img} style={{ objectPosition: meal.pos }} />
                  : <img src={meal.img} alt={label} loading="lazy" decoding="async" style={{ objectPosition: meal.pos }} />}
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
        <p className="eat-manifest">
          <span>{copy.manifest[0]}</span>
          <span>{copy.manifest[1]}</span>
          <span className="is-on">{copy.manifest[2]}</span>
        </p>
      </section>

      <section className="eat-section" id="how">
        <h2 className="eat-h2">{copy.howTitle}</h2>
        <ol className="eat-steps">
          {copy.steps.map(([title, line], index) => <li key={title}><b>{index + 1}</b><h3>{title}</h3><p>{line}</p></li>)}
        </ol>
      </section>

      <section className="eat-section eat-gaut" id="gaut">
        <div className="eat-gaut-photos">
          {GAUT_PHOTOS.map((photo) => (
            <figure key={photo.src}>
              <img src={photo.src} alt={photo.alt[lang]} loading="lazy" decoding="async" style={{ objectPosition: photo.pos }} />
            </figure>
          ))}
        </div>
        <div className="eat-gaut-copy">
          <h2 className="eat-h2">{copy.gautTitle}</h2>
          {copy.gaut.map((line) => <p key={line}>{line}</p>)}
          <p className="eat-sign">Gaut</p>
        </div>
      </section>

      <section className="eat-section eat-pricing" id="pricing" ref={(node) => { endZone.current[0] = node; }}>
        <div className="eat-card">
          <h2 className="eat-h2">{copy.pricingTitle}</h2>
          <p className="eat-card-sub">{copy.pricingSub}</p>
          <p className="eat-card-price">{deal?.was && <s>{deal.was}</s>}<b>{price.label}</b><span>{copy.perMonth}</span></p>
          {deal && <p className="eat-card-regular">{deal.was ? copy.off(deal) : copy.regular(deal)}</p>}
          {deal && (
            <div className="eat-deal">
              <p className="eat-deal-clock"><span><i aria-hidden="true" />{copy.goesUpIn}</span><b className="eat-clock">{leftLabel(deal.ms, copy.clock)}</b></p>
              <ul>{copy.dealPoints(deal).map((point, index) => <li key={index}>{point}</li>)}</ul>
            </div>
          )}
          {cta('eat-price', copy.getAccess, deal ? 'eat-btn-block is-live' : 'eat-btn-block')}
          {!deal && <p className="eat-micro">{copy.cancelAnytime}</p>}
        </div>
      </section>

      <section className="eat-section eat-faq" id="faq">
        <h2 className="eat-h2">{copy.faqTitle}</h2>
        <div className="eat-qa-list">
          {copy.faq.map(([question, answer], index) => {
            const open = openFaq === index;
            return (
              <div key={question} className={open ? 'eat-qa is-open' : 'eat-qa'}>
                <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? -1 : index)}>
                  {question}<i aria-hidden="true" />
                </button>
                {open && <p>{answer}</p>}
              </div>
            );
          })}
        </div>
      </section>

      <section className="eat-final" id="final" ref={(node) => { endZone.current[1] = node; }}>
        <img className="eat-final-bg" src="/eat/pancakes-berries.jpg" alt="" aria-hidden="true" loading="lazy" />
        <div className="eat-final-copy">
          <h2 className="eat-h2">{copy.finalTitle}</h2>
          <p className="eat-price">{deal?.was && <s>{deal.was}</s>}<b>{price.label}</b> {copy.perMonth}{deal ? ` · ${copy.finalUp(deal)}` : ''}</p>
          {cta('eat-final', copy.join, deal ? 'is-live' : '')}
          <p className="eat-micro">{deal ? copy.oneCharge : copy.micro}</p>
        </div>
      </section>

      <section className="eat-elite" id="elite" ref={(node) => { endZone.current[2] = node; }}>
        <p className="eat-elite-kicker">OpenBro Elite</p>
        <h2 className="eat-h2">{copy.elite.title}</h2>
        <p className="eat-elite-text">{copy.elite.text}</p>
        <a href="/elite" className="eat-elite-campus" tabIndex={-1}>
          {ELITE_CAMPUS.map((campus) => (
            <img key={campus.src} src={lang === 'en' && campus.srcEn ? campus.srcEn : campus.src} alt={campus.alt[lang]} loading="lazy" decoding="async" />
          ))}
        </a>
        <a href="/elite" className="eat-elite-btn">{copy.elite.cta}<Arrow /></a>
      </section>

      <footer className="eat-footer">
        <img src="/logo-openbro.png" alt="OpenBro" />
        <nav>
          <a href="/elite">OpenBro Elite</a>
          <Link to="/terms">{copy.terms}</Link>
          <Link to="/privacy">{copy.privacy}</Link>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </nav>
      </footer>

      <div className={sticky ? 'eat-sticky is-on' : 'eat-sticky'} aria-hidden={!sticky}>
        <p><span>{deal?.was && <s>{deal.was}</s>}<b>{price.label}</b> {copy.perMonth}</span>{deal ? <small>{copy.stickyUp} {leftShort(deal.ms, copy.clock)}</small> : null}</p>
        <button type="button" className="eat-btn" tabIndex={sticky ? 0 : -1} disabled={isActive} onClick={() => join('eat-sticky')}>
          {isActive ? copy.openingShort : copy.start}<Arrow />
        </button>
      </div>
    </div>
  );
}
